import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import DataTable from 'react-data-table-component';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import { MdHistory, MdDevices, MdRefresh, MdErrorOutline } from 'react-icons/md';
import {
    fetchAdminNotificationLogs,
    fetchAdminNotificationLogStats,
    fetchAdminNotificationDeviceUsers,
} from '../../../services/Http/AdminApi';

const Toolbar = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 1rem;
`;

const SearchInput = styled.input`
    flex: 1 1 240px;
    min-width: 200px;
    padding: 0.5625rem 0.75rem;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
`;

const ToggleButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5625rem 0.875rem;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.surface)};
    color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.textPrimary)};
    font-size: 0.8125rem;
`;

const Pill = styled.span`
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: ${({ $tone, theme }) => {
        if ($tone === 'bad') return `${theme.colors.error || '#dc2626'}22`;
        if ($tone === 'good') return '#16a34a22';
        if ($tone === 'warn') return '#f59e0b22';
        return theme.colors.tableHeaderBg || '#f3f4f6';
    }};
    color: ${({ $tone, theme }) => {
        if ($tone === 'bad') return theme.colors.error || '#dc2626';
        if ($tone === 'good') return '#16a34a';
        if ($tone === 'warn') return '#b45309';
        return theme.colors.textMuted;
    }};
`;

const Detail = styled.div`
    padding: 0.875rem 1.125rem;
    background: ${({ theme }) => theme.colors.tableHeaderBg || '#f9fafb'};
    border-left: 1px solid ${({ theme }) => theme.colors.border};
    border-right: 1px solid ${({ theme }) => theme.colors.border};
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.colors.textPrimary};

    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 0.375rem 0.625rem; }
    th { color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.6875rem; text-transform: uppercase; }
    tr + tr td { border-top: 1px solid ${({ theme }) => theme.colors.border}; }
    pre {
        margin: 0.5rem 0 0;
        padding: 0.625rem;
        border-radius: 8px;
        overflow-x: auto;
        background: ${({ theme }) => theme.colors.surface};
        border: 1px solid ${({ theme }) => theme.colors.border};
        font-size: 0.75rem;
    }
`;

const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

/**
 * Read-only history of what was sent and whether it landed.
 *
 * The two tabs answer the two questions that actually get asked: "did this
 * person get the message" and "can this person be reached at all". The second
 * matters more than it looks — a user can have a dozen registered devices and
 * be completely unreachable if every token has been revoked or gone stale, and
 * nothing in the product made that visible.
 */
const AdminNotificationLogs = ({ defaultTab = 'logs' }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(defaultTab);

    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsPage, setLogsPage] = useState(1);
    const [logsPerPage, setLogsPerPage] = useState(25);
    const [search, setSearch] = useState('');
    const [failedOnly, setFailedOnly] = useState(false);

    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(25);
    const [userSearch, setUserSearch] = useState('');
    const [unreachableOnly, setUnreachableOnly] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const tableStyles = useMemo(() => getAdminTableStyles(theme), [theme]);

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [logRes, statRes] = await Promise.all([
                fetchAdminNotificationLogs({
                    page: logsPage,
                    per_page: logsPerPage,
                    search: search || undefined,
                    failed_only: failedOnly ? 1 : undefined,
                }),
                fetchAdminNotificationLogStats(),
            ]);
            setLogs(logRes.data || []);
            setLogsTotal(logRes.total || 0);
            setStats(statRes);
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [logsPage, logsPerPage, search, failedOnly]);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchAdminNotificationDeviceUsers({
                page: usersPage,
                per_page: usersPerPage,
                search: userSearch || undefined,
                unreachable_only: unreachableOnly ? 1 : undefined,
            });
            setUsers(res.data || []);
            setUsersTotal(res.total || 0);
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [usersPage, usersPerPage, userSearch, unreachableOnly]);

    useEffect(() => {
        if (activeTab === 'logs') loadLogs();
        else loadUsers();
    }, [activeTab, loadLogs, loadUsers]);

    const logColumns = useMemo(() => [
        {
            name: t('AdminNotificationLogs.columns.sent'),
            selector: (row) => row.created_at,
            format: (row) => formatDateTime(row.created_at),
            sortable: true,
            width: '170px',
        },
        {
            name: t('AdminNotificationLogs.columns.type'),
            selector: (row) => row.type,
            width: '190px',
        },
        {
            name: t('AdminNotificationLogs.columns.title'),
            selector: (row) => row.title,
            grow: 2,
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.title}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{row.body}</div>
                </div>
            ),
        },
        {
            name: t('AdminNotificationLogs.columns.user'),
            selector: (row) => row.user?.email || '-',
            grow: 1,
        },
        {
            name: t('AdminNotificationLogs.columns.devices'),
            width: '150px',
            cell: (row) => {
                // The per-device outcome is the whole point: "sent" only means
                // we handed it to the provider.
                if (!row.devices_targeted) return <Pill $tone="warn">{t('AdminNotificationLogs.noDevice')}</Pill>;
                if (row.devices_failed === row.devices_targeted) return <Pill $tone="bad">{t('AdminNotificationLogs.failed')}</Pill>;
                if (row.devices_opened > 0) return <Pill $tone="good">{t('AdminNotificationLogs.opened')}</Pill>;
                return <Pill>{t('AdminNotificationLogs.sent')}</Pill>;
            },
        },
    ], [t]);

    const userColumns = useMemo(() => [
        {
            name: t('AdminNotificationLogs.columns.user'),
            selector: (row) => row.email,
            grow: 2,
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{`${row.name || ''} ${row.surname || ''}`.trim() || row.email}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{row.email}</div>
                </div>
            ),
        },
        {
            name: t('AdminNotificationLogs.columns.devicesTotal'),
            selector: (row) => Number(row.devices_total),
            sortable: true,
            width: '130px',
        },
        {
            name: t('AdminNotificationLogs.columns.reachable'),
            width: '150px',
            cell: (row) => (Number(row.devices_reachable) > 0
                ? <Pill $tone="good">{row.devices_reachable}</Pill>
                : <Pill $tone="bad">{t('AdminNotificationLogs.unreachable')}</Pill>),
        },
        {
            name: t('AdminNotificationLogs.columns.lastSeen'),
            selector: (row) => row.last_seen_at,
            format: (row) => formatDateTime(row.last_seen_at),
            sortable: true,
            width: '180px',
        },
    ], [t]);

    const LogDetail = useCallback(({ data }) => (
        <Detail>
            {data.failure_reason && (
                <div style={{ marginBottom: 10 }}>
                    <Pill $tone="bad">{t('AdminNotificationLogs.failed')}</Pill> {data.failure_reason}
                </div>
            )}
            <table>
                <thead>
                    <tr>
                        <th>{t('AdminNotificationLogs.columns.device')}</th>
                        <th>{t('AdminNotificationLogs.columns.status')}</th>
                        <th>{t('AdminNotificationLogs.columns.sent')}</th>
                        <th>{t('AdminNotificationLogs.opened')}</th>
                        <th>{t('AdminNotificationLogs.columns.error')}</th>
                    </tr>
                </thead>
                <tbody>
                    {(data.deliveries || []).map((d, i) => (
                        <tr key={i}>
                            <td>{[d.platform, d.device_model, d.app_version].filter(Boolean).join(' · ') || '-'}</td>
                            <td>
                                <Pill $tone={d.failed_at ? 'bad' : d.opened_at ? 'good' : undefined}>{d.status}</Pill>
                            </td>
                            <td>{formatDateTime(d.sent_at)}</td>
                            <td>{formatDateTime(d.opened_at)}</td>
                            <td>{d.error_code ? `${d.error_code}${d.error_message ? `: ${d.error_message}` : ''}` : '-'}</td>
                        </tr>
                    ))}
                    {(!data.deliveries || data.deliveries.length === 0) && (
                        <tr><td colSpan={5}>{t('AdminNotificationLogs.noDeliveries')}</td></tr>
                    )}
                </tbody>
            </table>
            {data.payload && <pre>{JSON.stringify(data.payload, null, 2)}</pre>}
        </Detail>
    ), [t]);

    const UserDetail = useCallback(({ data }) => (
        <Detail>
            <table>
                <thead>
                    <tr>
                        <th>{t('AdminNotificationLogs.columns.device')}</th>
                        <th>{t('AdminNotificationLogs.columns.status')}</th>
                        <th>{t('AdminNotificationLogs.columns.lastSeen')}</th>
                        <th>{t('AdminNotificationLogs.columns.registered')}</th>
                    </tr>
                </thead>
                <tbody>
                    {(data.devices || []).map((d) => (
                        <tr key={d.id}>
                            <td>{[d.platform, d.device_model, d.os_version, d.app_version].filter(Boolean).join(' · ')}</td>
                            <td>
                                {d.is_active && d.notifications_enabled
                                    ? <Pill $tone="good">{t('AdminNotificationLogs.reachable')}</Pill>
                                    : <Pill $tone="bad">{d.revoked_at ? t('AdminNotificationLogs.revoked') : t('AdminNotificationLogs.inactive')}</Pill>}
                            </td>
                            <td>{formatDateTime(d.last_seen_at)}</td>
                            <td>{formatDateTime(d.created_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Detail>
    ), [t]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminNotificationLogs.title')}</PageTitle>
            </PageHeader>

            <TabBar>
                <TabButton active={activeTab === 'logs'} onClick={() => setActiveTab('logs')} icon={<MdHistory className="icon" />}>
                    {t('AdminNotificationLogs.tabs.logs')}
                </TabButton>
                <TabButton active={activeTab === 'devices'} onClick={() => setActiveTab('devices')} icon={<MdDevices className="icon" />}>
                    {t('AdminNotificationLogs.tabs.devices')}
                </TabButton>
            </TabBar>

            <PageContent>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                {activeTab === 'logs' && (
                    <>
                        {stats && (
                            <StatsGrid>
                                <StatCard title={t('AdminNotificationLogs.stats.total')} value={stats.notifications_total} />
                                <StatCard title={t('AdminNotificationLogs.stats.sent')} value={stats.sent} />
                                <StatCard title={t('AdminNotificationLogs.stats.opened')} value={stats.opened} />
                                <StatCard title={t('AdminNotificationLogs.stats.failed')} value={stats.failed} />
                            </StatsGrid>
                        )}

                        <Toolbar>
                            <SearchInput
                                placeholder={t('AdminNotificationLogs.searchLogs')}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setLogsPage(1); }}
                            />
                            <ToggleButton $active={failedOnly} onClick={() => { setFailedOnly((v) => !v); setLogsPage(1); }}>
                                <MdErrorOutline /> {t('AdminNotificationLogs.failedOnly')}
                            </ToggleButton>
                            <ToggleButton onClick={loadLogs}><MdRefresh /> {t('AdminNotificationLogs.refresh')}</ToggleButton>
                        </Toolbar>

                        {loading ? <LoadingMessage /> : (
                            <DataTable
                                columns={logColumns}
                                data={logs}
                                customStyles={tableStyles}
                                expandableRows
                                expandableRowsComponent={LogDetail}
                                pagination
                                paginationServer
                                paginationTotalRows={logsTotal}
                                paginationDefaultPage={logsPage}
                                paginationPerPage={logsPerPage}
                                onChangePage={(page) => setLogsPage(page)}
                                onChangeRowsPerPage={(perPage) => { setLogsPerPage(perPage); setLogsPage(1); }}
                                noDataComponent={t('AdminNotificationLogs.empty')}
                            />
                        )}
                    </>
                )}

                {activeTab === 'devices' && (
                    <>
                        <Toolbar>
                            <SearchInput
                                placeholder={t('AdminNotificationLogs.searchUsers')}
                                value={userSearch}
                                onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                            />
                            <ToggleButton $active={unreachableOnly} onClick={() => { setUnreachableOnly((v) => !v); setUsersPage(1); }}>
                                <MdErrorOutline /> {t('AdminNotificationLogs.unreachableOnly')}
                            </ToggleButton>
                            <ToggleButton onClick={loadUsers}><MdRefresh /> {t('AdminNotificationLogs.refresh')}</ToggleButton>
                        </Toolbar>

                        {loading ? <LoadingMessage /> : (
                            <DataTable
                                columns={userColumns}
                                data={users}
                                customStyles={tableStyles}
                                expandableRows
                                expandableRowsComponent={UserDetail}
                                pagination
                                paginationServer
                                paginationTotalRows={usersTotal}
                                paginationDefaultPage={usersPage}
                                paginationPerPage={usersPerPage}
                                onChangePage={(page) => setUsersPage(page)}
                                onChangeRowsPerPage={(perPage) => { setUsersPerPage(perPage); setUsersPage(1); }}
                                noDataComponent={t('AdminNotificationLogs.empty')}
                            />
                        )}
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

const getAdminTableStyles = (theme) => ({
    headRow: {
        style: {
            backgroundColor: theme.colors.tableHeaderBg || '#f9fafb',
            border: `1px solid ${theme.colors.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            color: theme.colors.textMuted,
            fontWeight: 700,
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        },
    },
    rows: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            borderLeft: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            minHeight: '52px',
        },
    },
    pagination: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.border}`,
            borderTop: 'none',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
        },
    },
});

export default AdminNotificationLogs;
